import { type FormEvent, type ReactNode, useCallback, useRef, useState } from "react";
import { Button } from "./Button";
import { Dialog } from "./Dialog";
import { Field, TextInput } from "./Field";

export type AskTextOptions = {
  title: string;
  label: string;
  initial?: string;
  confirmLabel?: string;
  validate?: (value: string) => string | null;
};

export function usePrompt(): { ask: (options: AskTextOptions) => Promise<string | null>; element: ReactNode } {
  const [options, setOptions] = useState<AskTextOptions | null>(null);
  const [value, setValue] = useState("");
  const [error, setError] = useState<string | null>(null);
  const resolver = useRef<((v: string | null) => void) | null>(null);

  const ask = useCallback((o: AskTextOptions) => {
    resolver.current?.(null);
    setOptions(o);
    setValue(o.initial ?? "");
    setError(null);
    return new Promise<string | null>((resolve) => {
      resolver.current = resolve;
    });
  }, []);

  const finish = (result: string | null) => {
    resolver.current?.(result);
    resolver.current = null;
    setOptions(null);
  };
  const submit = (e: FormEvent) => {
    e.preventDefault();
    const problem = options?.validate?.(value) ?? null;
    if (problem) {
      setError(problem);
      return;
    }
    finish(value);
  };

  const element = (
    <Dialog open={options !== null} onClose={() => finish(null)} title={options?.title ?? ""} size="sm">
      <form onSubmit={submit} className="grid gap-4" noValidate>
        <Field label={options?.label ?? ""} error={error}>
          {(id, describedBy) => (
            <TextInput
              id={id}
              aria-describedby={describedBy}
              invalid={Boolean(error)}
              value={value}
              data-autofocus
              onChange={(e) => {
                setValue(e.target.value);
                setError(null);
              }}
            />
          )}
        </Field>
        <div className="flex justify-end gap-2">
          <Button onClick={() => finish(null)}>Cancel</Button>
          <Button type="submit" variant="primary">
            {options?.confirmLabel ?? "OK"}
          </Button>
        </div>
      </form>
    </Dialog>
  );
  return { ask, element };
}
